import React from 'react';
import { CircularProgress, Menu, MenuItem } from '@mui/material';
import { useDispatch } from 'react-redux';
import {
	useGetWorkspaceVersionsQuery,
	useRestoreWorkspacePageMutation,
} from '@/features/workspaces/workspacesApi';
import { showToast } from '@/features/toast/toastSlice';
import { formatDate } from '@/utils/constants';
import { useWorkspaceContext } from '@/context/WorkspaceContext';

const normalizeFilePath = (filePath = '') => filePath.replace(/^\/+/, '');

export const VersionHistoryMenu = ({ anchorEl, onClose, onRestore }) => {
	const { dealId, clientId, relationshipCompanyId, currentFilePath } = useWorkspaceContext();

	const dispatch = useDispatch();
	const open = Boolean(anchorEl);

	const {
		data: versions = [],
		isFetching: isVersionsLoading,
		isUninitialized: isVersionsUninitialized,
		isError: isVersionsError,
	} = useGetWorkspaceVersionsQuery(
		{ dealId, clientId, relationshipCompanyId },
		{ skip: (!clientId && !dealId && !relationshipCompanyId) || !open },
	);

	const [restoreWorkspacePage, { isLoading: isRestoring }] = useRestoreWorkspacePageMutation();

	const previousVersions = versions.filter(
		(version) => normalizeFilePath(version.filePath) !== normalizeFilePath(currentFilePath),
	);

	const handleRestore = async (version) => {
		try {
			await restoreWorkspacePage({
				dealId,
				clientId,
				relationshipCompanyId,
				versionId: version.id,
				fileName: version.fileName,
				filePath: version.filePath,
			}).unwrap();
			onRestore?.();
			onClose?.();
			dispatch(
				showToast({
					message: 'Workspace version restored successfully!',
					success: true,
				}),
			);
		} catch {
			dispatch(
				showToast({
					message: 'Error restoring workspace. Please try again.',
					error: true,
				}),
			);
		}
	};

	return (
		<Menu
			anchorEl={anchorEl}
			open={open}
			onClose={onClose}
			anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
			transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			PaperProps={{ sx: { maxHeight: 320, minWidth: 240 } }}
		>
			{isVersionsLoading || isVersionsUninitialized ? (
				<MenuItem disabled>
					<CircularProgress size={16} sx={{ mr: 1 }} />
					Loading...
				</MenuItem>
			) : isVersionsError ? (
				<MenuItem disabled>Unable to load versions</MenuItem>
			) : previousVersions.length === 0 ? (
				<MenuItem disabled>No previous versions</MenuItem>
			) : (
				previousVersions.map((version) => (
					<MenuItem
						key={version.id}
						disabled={isRestoring}
						onClick={() => handleRestore(version)}
					>
						{formatDate(version.createdAt, true)}
					</MenuItem>
				))
			)}
		</Menu>
	);
};

export default VersionHistoryMenu;
VersionHistoryMenu.displayName = 'VersionHistoryMenu';
